import axios from 'axios';

import {
  ContractItem,
  Contracts,
  ContractsQuery,
  IContractTemplateWithCollection,
  IResponseWithPagination,
  ITemplate,
  SIGN_MODE_ENUM
} from '@/types';
import { request, stringifyQueryString } from '@/utils';

export const queryFolders = async (): Promise<any> => {
  return request({
    url: '/contracts/folders',
    method: 'GET'
  }).then((res) => res);
};

export const createFolders = async (data: { folderName: string }): Promise<any> => {
  return request({
    url: '/contracts/folders',
    method: 'POST',
    data
  });
};
export const updateFolders = async (data: { id: number; folderName: string }): Promise<any> => {
  return request({
    url: `/contracts/folders/${data.id}`,
    method: 'PUT',
    data
  });
};
export const deleteFolders = async (data: { id: number }): Promise<any> => {
  return request({
    url: `/contracts/folders/${data.id}`,
    method: 'DELETE'
  });
};

export const queryContracts = async ({
  hidden,
  folder,
  page,
  size = 6,
  filter,
  keyword,
  contractType
}: ContractsQuery): Promise<Contracts> => {
  return request({
    url: `/contracts/query-v2`,
    method: 'GET',
    data: {
      hidden,
      folder: folder != undefined ? Number(folder) : undefined,
      page,
      size,
      filter,
      status: filter,
      keyword,
      type: contractType
    },
    params: { hidden, folder, page, size, filter, status: filter, keyword, type: contractType }
  }).then((res: any) => {
    const { page, size, total, rows } = res || {};
    return {
      page,
      size,
      total,
      data: rows.map(({ createdAt, clientStatus, ...props }: any) => ({
        ...props,
        createdAt,
        clientStatus,
        date: createdAt,
        status: clientStatus
      }))
    };
  });
};

export const hideContracts = async (data: { contracts: number[] }): Promise<any> => {
  return request({
    url: `/contracts/batch-hide`,
    method: 'POST',
    data
  });
};
export const unHideContracts = async (data: { contracts: number[] }): Promise<any> => {
  return request({
    url: `/contracts/batch-unhide`,
    method: 'POST',
    data
  });
};

export const moveContracts = async (data: { folderId?: number | undefined; contracts: number[] }): Promise<any> => {
  return request({
    url: '/contracts/move-to-folder',
    method: 'POST',
    data
  });
};

export const queryContract = async ({
  id,
  email,
  errorHandler,
  mode
}: {
  id: string;
  email?: string;
  mode?: string;
  errorHandler?: (e: any) => void;
}): Promise<ContractItem> => {
  const params = stringifyQueryString({ email, mode });
  return request({
    url: `/contracts/${id}${params ? '?' + params : ''}`,
    method: 'GET',
    errorHandler
  }).then((res: any) => {
    return res || {};
  });
};
// POST contracts/
export const createContract = async (data: {
  title: string;
  fileEncrypted: string;
  enableExpiration: boolean;
  expireTime?: number;
  enableEncryption: boolean;
  recipients: any[];
  folderId?: number | undefined;
  signMode?: SIGN_MODE_ENUM;
  templateId?: number;
  mailto?: string;
}): Promise<any> => {
  return request({
    url: '/contracts',
    method: 'POST',
    data
  });
};

export const queryContractFile = async ({
  id,
  email,
  errorHandler
}: {
  id: string;
  email: string;
  errorHandler: (e: any) => void;
}): Promise<{ fileContent: string }> => {
  const params = stringifyQueryString({ email });
  return request({
    url: `/contracts/${id}/file${params ? '?' + params : ''}`,
    method: 'GET',
    errorHandler
  }).then((res: any) => {
    return res || {};
  });
};

export const signContract = async (data: {
  signingData: string;
  signature: string;
  id: string;
  protocolTag?: string;
  addressEmailBindingEmail?: string;
  addressEmailBindingMessage?: string;
  addressEmailBindingSignature?: string;
  walletName: string;
  chainId?: string;
}): Promise<any> => {
  return request({
    url: `/contracts/${data.id}/signing`,
    method: 'POST',
    data
  });
};

export const getContractByID = async (id: string): Promise<any> => {
  return request({
    url: `/info/query-txid`,
    method: 'POST',
    data: {
      contractId: id
    }
  });
};

export const getContractDataByTx = (hash: string): Promise<any> => {
  return axios.post(`https://arweave.net/${hash}`);
};

export const updateEncryptedContractPassword = async (data: {
  id: string;
  encryptedContractPassword: string;
}): Promise<any> => {
  return request({
    url: `/contracts/${data.id}/encrypted-contract-password`,
    method: 'POST',
    data
  });
};

export const completeContract = async (data: { id: string }): Promise<any> => {
  return request({
    url: `/contracts/${data.id}/complete`,
    method: 'POST',
    data
  });
};

export const voidContract = async (data: { id: string }): Promise<any> => {
  return request({
    url: `/contracts/${data.id}/void`,
    method: 'POST',
    data
  });
};

// GET contracts/ES-NmmUXPUp6JnDR-cNr2FEz/download
export const downloadContract = async (data: { id: string }): Promise<any> => {
  return request({
    url: `/contracts/${data.id}/download`,
    method: 'GET'
  });
};

// contracts/templates?keyword=&categoryId=1
export const queryContractTemplates = async (
  params?: Record<string, any>
): Promise<IResponseWithPagination<IContractTemplateWithCollection>> => {
  return request({
    url: '/contracts/templates',
    method: 'GET',
    params
  });
};

//contracts/templates/3
export const queryContractTemplate = async (id: string): Promise<ITemplate> => {
  return request({
    url: `/contracts/templates/${id}`,
    method: 'GET'
  });
};
//contracts/${id}/recipients?page=1
export const queryRecipients = async (id: string, page: number): Promise<any> => {
  return request({
    url: `/contracts/${id}/recipients`,
    method: 'GET',
    params: { page }
  });
};

//contracts/ES-V-rZ-Pxeeteb99o1WStZFMQ/export-recipients
export const exportRecipients = async (id: string): Promise<any> => {
  return request({
    url: `/contracts/${id}/export-recipients`,
    method: 'GET'
  });
};
